/**
 * WebAuthn/Passkey Utilities (Shared)
 *
 * Platform-wide helper functions for WebAuthn credential handling.
 * Uses SimpleWebAuthn v13+ browser package.
 *
 * Extracted from Gazian auth utilities for platform-wide use.
 *
 * @module lib/api/auth/passkey
 */

import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startRegistration,
  startAuthentication,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser';

// Re-export types for consumers
export type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
};

// ─────────────────────────────────────────────────────────────────────────────
// Browser Support Detection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check if the current browser supports WebAuthn/Passkeys
 *
 * @returns true if browser supports WebAuthn credential creation/authentication
 */
export function isPasskeySupported(): boolean {
  return browserSupportsWebAuthn();
}

/**
 * Check if browser supports conditional UI (autofill passkeys)
 *
 * This allows users to select passkeys from the browser's autofill dropdown
 * when focusing on an input with `autocomplete="webauthn"`
 *
 * @returns Promise<boolean> - true if conditional UI is supported
 */
export async function isConditionalUISupported(): Promise<boolean> {
  return browserSupportsWebAuthnAutofill();
}

// ─────────────────────────────────────────────────────────────────────────────
// Registration Flow
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Start passkey registration ceremony
 *
 * IMPORTANT: Must be called from a user gesture (click handler) context.
 * The browser will show a biometric prompt (Face ID, fingerprint, etc.)
 *
 * @param options - Registration options from server's generateRegistrationOptions()
 * @returns Promise<RegistrationResponseJSON> - Credential to send to server for verification
 * @throws Error if user cancels or WebAuthn fails
 *
 * @example
 * ```tsx
 * const handleRegister = async () => {
 *   const options = await api.getRegistrationOptions(email);
 *   const credential = await registerPasskey(options);
 *   await api.verifyRegistration(email, credential);
 * };
 * ```
 */
export async function registerPasskey(
  options: PublicKeyCredentialCreationOptionsJSON
): Promise<RegistrationResponseJSON> {
  // v13+ API: Pass options wrapped in optionsJSON property
  const credential = await startRegistration({ optionsJSON: options });
  return credential;
}

// ─────────────────────────────────────────────────────────────────────────────
// Authentication Flow
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Start passkey authentication ceremony
 *
 * IMPORTANT: Must be called from a user gesture (click handler) context.
 * The browser will show a biometric prompt to verify identity.
 *
 * @param options - Authentication options from server's generateAuthenticationOptions()
 * @returns Promise<AuthenticationResponseJSON> - Assertion to send to server for verification
 * @throws Error if user cancels or WebAuthn fails
 *
 * @example
 * ```tsx
 * const handleAuthenticate = async () => {
 *   const options = await api.getPasskeyLoginOptions(email);
 *   const assertion = await authenticateWithPasskey(options);
 *   const session = await api.verifyPasskeyLogin(email, assertion);
 * };
 * ```
 */
export async function authenticateWithPasskey(
  options: PublicKeyCredentialRequestOptionsJSON
): Promise<AuthenticationResponseJSON> {
  // v13+ API: Pass options wrapped in optionsJSON property
  const assertion = await startAuthentication({ optionsJSON: options });
  return assertion;
}

/**
 * Start passkey authentication with browser autofill (conditional UI)
 *
 * This allows passkeys to appear in the browser's autofill dropdown.
 * Should be called on page load, not in a click handler.
 *
 * Requires an input element with `autocomplete="webauthn"` attribute.
 *
 * @param options - Authentication options from server
 * @returns Promise<AuthenticationResponseJSON> - Assertion when user selects a passkey
 * @throws Error if conditional UI not supported or user cancels
 *
 * @example
 * ```tsx
 * useEffect(() => {
 *   if (await isConditionalUISupported()) {
 *     const options = await api.getAuthOptions();
 *     const assertion = await authenticateWithAutofill(options);
 *     // User selected a passkey from autofill
 *   }
 * }, []);
 * ```
 */
export async function authenticateWithAutofill(
  options: PublicKeyCredentialRequestOptionsJSON
): Promise<AuthenticationResponseJSON> {
  const assertion = await startAuthentication({
    optionsJSON: options,
    useBrowserAutofill: true,
  });
  return assertion;
}

// ─────────────────────────────────────────────────────────────────────────────
// Error Handling
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Error codes for passkey operations
 */
export type PasskeyErrorCode =
  | 'NOT_SUPPORTED' // Browser doesn't support WebAuthn
  | 'USER_CANCELLED' // User dismissed the prompt
  | 'ALREADY_REGISTERED' // Authenticator already registered
  | 'NO_PASSKEYS' // No passkeys registered for this email
  | 'TIMEOUT' // Operation timed out
  | 'INVALID_STATE' // Invalid state (e.g., trying to re-register)
  | 'NETWORK_ERROR' // Network request failed
  | 'UNKNOWN'; // Unknown error

/**
 * Structured error for passkey operations
 */
export interface PasskeyError {
  code: PasskeyErrorCode;
  message: string;
  originalError?: Error;
}

/**
 * Parse WebAuthn errors into user-friendly messages
 *
 * @param error - Error from WebAuthn operation
 * @returns PasskeyError with code and user-friendly message
 */
export function parsePasskeyError(error: unknown): PasskeyError {
  if (!(error instanceof Error)) {
    return {
      code: 'UNKNOWN',
      message: 'An unexpected error occurred. Please try again.',
    };
  }

  const errorName = error.name;
  const errorMessage = error.message.toLowerCase();

  // API-level error: No passkeys registered for this email
  if (errorMessage.includes('no passkey') || errorMessage.includes('not registered')) {
    return {
      code: 'NO_PASSKEYS',
      message: 'No passkeys registered for this email. Please use email verification instead.',
      originalError: error,
    };
  }

  // User cancelled the operation
  if (errorName === 'NotAllowedError') {
    if (errorMessage.includes('cancelled') || errorMessage.includes('canceled')) {
      return {
        code: 'USER_CANCELLED',
        message: 'Authentication was cancelled. Please try again.',
        originalError: error,
      };
    }
    return {
      code: 'USER_CANCELLED',
      message: 'Authentication was not allowed. Please try again.',
      originalError: error,
    };
  }

  // Authenticator already registered
  if (errorName === 'InvalidStateError') {
    return {
      code: 'ALREADY_REGISTERED',
      message: 'This device is already registered. Try signing in instead.',
      originalError: error,
    };
  }

  // Operation timed out
  if (errorName === 'AbortError' || errorMessage.includes('timeout')) {
    return {
      code: 'TIMEOUT',
      message: 'The operation timed out. Please try again.',
      originalError: error,
    };
  }

  // Not supported
  if (errorName === 'NotSupportedError') {
    return {
      code: 'NOT_SUPPORTED',
      message: 'Passkeys are not supported on this device.',
      originalError: error,
    };
  }

  // Network error
  if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
    return {
      code: 'NETWORK_ERROR',
      message: 'Network error. Please check your connection and try again.',
      originalError: error,
    };
  }

  // Unknown error
  return {
    code: 'UNKNOWN',
    message: 'Something went wrong. Please try again or use a verification code.',
    originalError: error,
  };
}
